import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck, UserMinus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiClientError, api } from "../lib/api";
import { whenever } from "../lib/format";
import i18n from "../lib/i18n";
import { useSession } from "../lib/useSession";

interface Module {
  key: string;
  labelKey: string;
}
interface AccessRequest {
  id: string;
  email: string;
  name: string | null;
  scopes: string[];
  note: string | null;
  status: "pending" | "approved" | "denied";
  decidedBy: string | null;
  decidedAt: string | null;
  requestedAt: string;
}
interface AllowedUser {
  id: string;
  email: string;
  scopes: string[];
  grantedBy: string | null;
  note: string | null;
  grantedAt: string;
}

const STATUS_VARIANT = {
  pending: "warning",
  approved: "success",
  denied: "destructive",
} as const;

/**
 * The GAC admin surface (stack §7c). Per the stack's own note, this page trusts
 * the "*" scope as its only admin check — which is precisely why "*" is granted
 * sparingly. The server enforces the same thing independently; hiding the nav
 * link is a courtesy, not the control.
 */
export default function AccessAdmin() {
  const { t } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "en";
  const qc = useQueryClient();
  const { user } = useSession();
  const isAdmin = user?.scopes?.includes("*") ?? false;

  const [email, setEmail] = useState("");
  const [picked, setPicked] = useState<string[]>([]);

  const modulesQ = useQuery({
    queryKey: ["gac", "modules"],
    queryFn: () => api<Module[]>("/auth/modules"),
  });
  const requestsQ = useQuery({
    queryKey: ["gac", "requests"],
    queryFn: () => api<AccessRequest[]>("/api/v1/access/requests"),
    enabled: isAdmin,
  });
  const usersQ = useQuery({
    queryKey: ["gac", "users"],
    queryFn: () => api<AllowedUser[]>("/api/v1/access/users"),
    enabled: isAdmin,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["gac", "requests"] });
    void qc.invalidateQueries({ queryKey: ["gac", "users"] });
  };

  const decide = useMutation({
    mutationFn: ({ id, approve }: { id: string; approve: boolean }) =>
      api(`/api/v1/access/requests/${id}/decide`, {
        method: "POST",
        body: JSON.stringify({ approve }),
      }),
    onSuccess: invalidate,
  });

  const grant = useMutation({
    mutationFn: () =>
      api("/api/v1/access/grants", {
        method: "POST",
        body: JSON.stringify({ email: email.trim(), scopes: picked }),
      }),
    onSuccess: () => {
      setEmail("");
      setPicked([]);
      invalidate();
    },
  });

  const revoke = useMutation({
    mutationFn: (addr: string) =>
      api(`/api/v1/access/users/${encodeURIComponent(addr)}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });

  if (!isAdmin) {
    return (
      <p className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
        {t("gac:adminOnly")}
      </p>
    );
  }

  const pending = (requestsQ.data ?? []).filter((r) => r.status === "pending");
  const decided = (requestsQ.data ?? []).filter((r) => r.status !== "pending").slice(0, 25);
  const modules = modulesQ.data ?? [];

  return (
    <>
      <div className="mb-6">
        <h1>{t("gac:adminTitle")}</h1>
        <p className="mt-2 text-muted-foreground">{t("gac:adminSubtitle")}</p>
      </div>

      <Card className="mb-6">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-base">
            {t("gac:pendingRequests", { count: pending.length })}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          {pending.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("gac:noPending")}</p>
          ) : (
            <Table className="min-w-[42rem]">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("gac:who")}</TableHead>
                  <TableHead>{t("gac:asked")}</TableHead>
                  <TableHead>{t("gac:why")}</TableHead>
                  <TableHead className="text-right">{t("gac:decide")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pending.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <span className="block">{r.name ?? "—"}</span>
                      <span className="block font-mono text-xs text-muted-foreground">
                        {r.email}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        {whenever(r.requestedAt, locale)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="flex flex-wrap gap-1">
                        {r.scopes.map((sc) => (
                          <Badge key={sc} variant="secondary">
                            {sc}
                          </Badge>
                        ))}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.note ?? "—"}</TableCell>
                    <TableCell className="text-right">
                      <span className="inline-flex gap-2">
                        <Button
                          size="sm"
                          disabled={decide.isPending}
                          onClick={() => decide.mutate({ id: r.id, approve: true })}
                        >
                          {t("gac:approve")}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={decide.isPending}
                          onClick={() => decide.mutate({ id: r.id, approve: false })}
                        >
                          {t("gac:deny")}
                        </Button>
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-4 w-4" aria-hidden />
            {t("gac:grantDirectly")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 p-4 pt-0">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <Label htmlFor="grant-email">{t("gac:emailLabel")}</Label>
              <Input
                id="grant-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@mitechnologiesinc.com"
                className="mt-1"
              />
            </div>
            <Button
              disabled={!email.trim() || picked.length === 0 || grant.isPending}
              onClick={() => grant.mutate()}
            >
              {t("gac:grant")}
            </Button>
          </div>
          <fieldset className="flex flex-wrap gap-4 border-0 p-0">
            <legend className="sr-only">{t("gac:whichAreas")}</legend>
            {modules.map((m) => (
              <span key={m.key} className="flex items-center gap-2">
                <Checkbox
                  id={`grant-${m.key}`}
                  checked={picked.includes(m.key)}
                  onCheckedChange={(v) =>
                    setPicked((prev) =>
                      v === true ? [...new Set([...prev, m.key])] : prev.filter((k) => k !== m.key),
                    )
                  }
                />
                <Label htmlFor={`grant-${m.key}`}>{t(m.labelKey)}</Label>
              </span>
            ))}
          </fieldset>
          <p className="text-xs text-muted-foreground">{t("gac:grantNote")}</p>
          {grant.isError && (
            <p className="text-sm text-destructive">
              {grant.error instanceof ApiClientError ? grant.error.message : t("gac:submitFailed")}
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-base">{t("gac:currentGrants")}</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <Table className="min-w-[38rem]">
            <TableHeader>
              <TableRow>
                <TableHead>{t("gac:emailLabel")}</TableHead>
                <TableHead>{t("gac:scopes")}</TableHead>
                <TableHead>{t("gac:grantedBy")}</TableHead>
                <TableHead className="text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(usersQ.data ?? []).map((u) => (
                <TableRow key={u.id}>
                  <TableCell className="font-mono text-xs">{u.email}</TableCell>
                  <TableCell>
                    <span className="flex flex-wrap gap-1">
                      {u.scopes.length === 0 ? (
                        <Badge variant="outline">{t("gac:revoked")}</Badge>
                      ) : (
                        u.scopes.map((sc) => (
                          <Badge key={sc} variant={sc === "*" ? "default" : "secondary"}>
                            {sc}
                          </Badge>
                        ))
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {u.grantedBy ?? "—"}
                    <span className="block">{whenever(u.grantedAt, locale)}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    {u.email !== user?.emailCanonical && u.scopes.length > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={revoke.isPending}
                        onClick={() => revoke.mutate(u.email)}
                      >
                        <UserMinus className="h-4 w-4" aria-hidden />
                        {t("gac:revoke")}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {decided.length > 0 && (
        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-base">{t("gac:recentDecisions")}</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <Table className="min-w-[34rem]">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("gac:who")}</TableHead>
                  <TableHead>{t("marketplaces:status")}</TableHead>
                  <TableHead>{t("gac:decidedBy")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {decided.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-xs">{r.email}</TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[r.status]}>
                        {t(`gac:status.${r.status}`)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {r.decidedBy ?? "—"}
                      <span className="block">{whenever(r.decidedAt, locale)}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </>
  );
}
